import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@monksflow/monks-ui';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { useTranslation } from 'react-i18next';
import { BUNDLE_EXTENSIONS, PLAN_EXTENSIONS } from '../lib/limits';
import type { Selection } from '../lib/split-selection';
import { PlanFilesField } from './PlanFilesField';
import { SourceFileField } from './SourceFileField';

export interface NewJobSubmit {
  title: string;
  /** The sheet itself: the server parses it, so it has to travel. */
  file: File;
  /** The creatives the plan names, uploaded once the job id exists. */
  creatives: File[];
}

export interface NewJobDialogProps {
  open: boolean;
  isSubmitting: boolean;
  /** Fraction of a zip sent to storage so far, while it is being sent. */
  uploadProgress?: number;
  onSubmit: (input: NewJobSubmit) => void;
  onClose: () => void;
}

/** The name a job defaults to: the plan's, with the extension and separators taken out. */
const titleFrom = (plan: File): string => {
  const stripped = [...PLAN_EXTENSIONS, ...BUNDLE_EXTENSIONS].reduce(
    (name, extension) =>
      name.toLowerCase().endsWith(extension) ? name.slice(0, -extension.length) : name,
    plan.name,
  );
  return stripped.replace(/[_-]+/g, ' ');
};

/**
 * Starting a Job asks for a title and files, and nothing else.
 *
 * Platform is deliberately not asked for: one plan routinely spans several, and
 * the file already says which. Asking would either be a lie we then contradict,
 * or a filter that silently drops half the plan.
 */
export function NewJobDialog({
  open,
  isSubmitting,
  uploadProgress,
  onSubmit,
  onClose,
}: NewJobDialogProps) {
  const { t } = useTranslation('projects');
  const looseCreatives = useBooleanFlagValue('delivery_multi_file_job_create', false);
  const [title, setTitle] = useState('');
  const [selection, setSelection] = useState<Selection | null>(null);

  const close = () => {
    setTitle('');
    setSelection(null);
    onClose();
  };

  const take = (next: Selection | null) => {
    setSelection(next);
    if (!next) return;

    // Read off the plan rather than the first file of the selection, so a drop that leads
    // with an image does not name the job after it.
    if (title.trim() === '') setTitle(titleFrom(next.plan));
  };

  // Deliberately no reset here: the upload is still in flight and the parent closes
  // this dialog itself once it succeeds. Clearing now wipes the title and the chosen
  // files while the request is running, so a failure toast lands on an empty form and
  // the operator has to find the spreadsheet again.
  const submit = () => {
    if (!selection || title.trim() === '') return;
    onSubmit({ title: title.trim(), file: selection.plan, creatives: selection.creatives });
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent className="w-[min(calc(100vw-2rem),34rem)]" data-testid="new-job-dialog">
        <DialogHeader>
          <DialogTitle>{t('deliveryJobCreate.newJobDialog.newJob')}</DialogTitle>
          <DialogDescription>
            {t('deliveryJobCreate.newJobDialog.uploadTheMediaPlan')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-job-title">{t('deliveryJobCreate.newJobDialog.title')}</Label>
            <Input
              id="new-job-title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder={t('deliveryJobCreate.newJobDialog.titlePlaceholder')}
              data-testid="new-job-title"
            />
          </div>

          {looseCreatives ? (
            <PlanFilesField onChange={take} />
          ) : (
            <SourceFileField
              file={selection?.plan ?? null}
              onSelect={(file) => take(file && { plan: file, creatives: [] })}
            />
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" size="default" onClick={close}>
            {t('deliveryJobCreate.newJobDialog.cancel')}
          </Button>
          <Button
            size="default"
            onClick={submit}
            disabled={!selection || title.trim() === '' || isSubmitting}
            data-testid="new-job-submit"
          >
            {!isSubmitting
              ? t('deliveryJobCreate.newJobDialog.parseFile')
              : uploadProgress === undefined
                ? t('deliveryJobCreate.newJobDialog.parsing')
                : t('deliveryJobCreate.newJobDialog.uploadingPercent', {
                    percent: Math.floor(uploadProgress * 100),
                  })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
